import { useEffect } from "react";
import { motion } from "framer-motion";
import Button from "./Button";

const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  bodyClassName = "",
  dialogClassName = "",
  footer,
  primaryButtonText,
  primaryButtonOnClick,
  primaryButtonVariant = "primary",
  primaryButtonDisabled = false,
  isLoading = false,
  secondaryButtonText,
  secondaryButtonOnClick,
  showCancelButton = true,
  cancelButtonText = "Huy",
  maxWidth = "600px",
  maxHeight = "90vh",
}) => {
  useEffect(() => {
    if (!isOpen) return undefined;

    const prev = document.body.style.overflow;
    const prevPaddingRight = document.body.style.paddingRight;
    const prevScrollbarGutter = document.documentElement.style.scrollbarGutter;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    document.documentElement.style.scrollbarGutter = "stable";
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = prev;
      document.body.style.paddingRight = prevPaddingRight;
      document.documentElement.style.scrollbarGutter = prevScrollbarGutter;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const renderFooter = () => {
    if (footer) {
      return <div className="px-6 py-4 border-t border-gray-200">{footer}</div>;
    }

    if (!primaryButtonText && !secondaryButtonText && !showCancelButton) {
      return null;
    }

    return (
      <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4">
        {showCancelButton && (
          <Button
            variant="secondary"
            size="lg"
            onClick={onClose}
            disabled={isLoading}
          >
            {cancelButtonText}
          </Button>
        )}
        {secondaryButtonText && (
          <Button
            variant="secondary"
            size="lg"
            onClick={secondaryButtonOnClick}
            disabled={isLoading}
          >
            {secondaryButtonText}
          </Button>
        )}
        {primaryButtonText && (
          <Button
            variant={primaryButtonVariant}
            size="lg"
            onClick={primaryButtonOnClick}
            disabled={primaryButtonDisabled || isLoading}
            isLoading={isLoading}
          >
            {primaryButtonText}
          </Button>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[70]">
      <motion.div
        className="absolute inset-0 bg-black/50"
        onClick={isLoading ? undefined : onClose}
        aria-hidden
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.16 }}
      />

      <div className="relative z-[71] flex h-full w-full items-center justify-center p-4">
        <motion.div
          className={`flex w-full flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ${dialogClassName}`.trim()}
          style={{ maxWidth, maxHeight }}
          initial={{ opacity: 0, scale: 0.98, y: 8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.18, ease: "easeOut" }}
        >
          <div className="flex items-center justify-between rounded-t-2xl border-b border-gray-200 px-6 py-5">
            <h2 className="text-xl font-bold text-gray-900">{title}</h2>
            <button
              onClick={onClose}
              disabled={isLoading}
              className="flex h-6 w-6 items-center justify-center text-gray-400 transition-colors hover:text-gray-600 disabled:opacity-50"
              aria-label="Close"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className={`flex-1 overflow-y-auto ${bodyClassName}`.trim()}>
            {children}
          </div>

          {renderFooter()}
        </motion.div>
      </div>
    </div>
  );
};

export default Modal;
